import { Link } from 'react-router-dom'
import Avatar from './Avatar.jsx'
import EventActionBar from './EventActionBar.jsx'
import { excerpt, conferenceLabel, talkTitleWithinConference } from '../lib/text.js'
import { speakerHref } from '../lib/permalinks.js'

// #350: the body of ONE event — abstract, speakers, calendar/social bar.
//
// A conference card and a standalone event card had drifted into two visual
// languages for the same thing: the standalone card wrapped its abstract over
// several lines and rendered its speaker at roughly twice the weight (an h-9
// avatar and a text-lg name) of the identical speaker inside a conference.
// Both cards now render this, so they cannot drift again.
//
// `showTitle` is the only difference between the two callers: a nested talk
// needs its own title, a standalone event already has it as the card heading.

export default function TalkBody({ talk, year, showTitle = true }) {
  const main = (talk.speakers || []).find((s) => s.isMain)
  const co = (talk.speakers || []).filter((s) => !s.isMain)
  const summary = excerpt(talk.abstractText, 140)
  const detailHref = `/events/${talk.id}`
  // #353: inside a conference the heading already carries the conference name,
  // so a talk titled the same thing adds a line that says nothing. Returns null
  // when there is nothing left to say, or the distinctive half of a
  // "<conference> — <talk>" title.
  const heading = talkTitleWithinConference(conferenceLabel(talk), talk.title)
  const renderTitle = showTitle && Boolean(heading)

  return (
    <>
      {renderTitle ? (
        <Link
          to={detailHref}
          className="relative z-10 text-base font-medium leading-snug text-slate-900 hover:text-brand-700"
        >
          {heading}
        </Link>
      ) : null}
      {/* Exactly one line at every width: the summary truncates inside its
          own min-w-0 box and `[more]` is shrink-0, so the link is always the
          last thing on the line instead of being the first thing clipped. */}
      {summary ? (
        <div className={`relative z-10 flex items-baseline gap-1 text-sm text-slate-600 ${renderTitle ? 'mt-0.5' : 'mt-2'}`}>
          <span className="min-w-0 truncate">{summary}</span>
          <Link
            to={detailHref}
            className="shrink-0 font-medium text-brand-700 hover:underline"
          >
            [more]
          </Link>
        </div>
      ) : null}
      {/* A talk may legitimately have no speaker yet — a line-up can hold
          slots before speakers are confirmed (#305). Render the slot without
          an avatar rather than a "User #null" placeholder. */}
      {main ? (
        <div className="relative z-10 mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-600">
          <Avatar
            gravatarHash={main.gravatarHash}
            email={main.email}
            displayName={main.displayName}
            size="h-5 w-5"
            textSize="text-[9px]"
          />
          <Link to={speakerHref(main.userId, year) || '/'} className="hover:text-brand-700 hover:underline">
            {main.displayName || `User #${main.userId}`}
          </Link>
          {co.length ? (
            <span className="text-slate-500">
              with{' '}
              {co.map((s, i) => (
                <span key={s.userId}>
                  {i > 0 ? ', ' : ''}
                  <Link to={speakerHref(s.userId, year) || '/'} className="hover:text-brand-700 hover:underline">
                    {s.displayName || `User #${s.userId}`}
                  </Link>
                </span>
              ))}
            </span>
          ) : null}
        </div>
      ) : (
        <div className="mt-1 text-xs italic text-slate-400">Speaker to be announced</div>
      )}
      <EventActionBar event={talk} />
    </>
  )
}
